import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  BadgeIndianRupee,
  Sparkles,
  Car,
  ArrowRight,
  Phone,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, BRANDS } from "../lib/gj";
import CarCard from "../components/CarCard";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/faebc29d-3b74-4c7b-9094-0a55a837aa6b/images/ff6e17b00cf241ac465f2d69fcd21f88ad933709c83459ce4ea51171c9258b0a.png";

const TRUST = [
  { icon: ShieldCheck, label: "Verified Dealers", sub: "Every dealer manually reviewed" },
  { icon: BadgeIndianRupee, label: "Fair Pricing", sub: "Transparent prices, no surprises" },
  { icon: Sparkles, label: "Quality Checked", sub: "Inspected listings across WB" },
];

const COMPARE = [
  {
    title: "Gaddi Junction",
    good: true,
    testid: "why-gj-column",
    points: ["0% brokerage on every deal", "Chat directly with sellers on WhatsApp", "Verified dealers, manually reviewed", "Transparent prices, no hidden charges"],
  },
  {
    title: "Traditional Brokers",
    good: false,
    testid: "why-brokers-column",
    points: ["High commissions on every sale", "Middlemen between you and the seller", "Unverified sellers and listings", "Hidden charges and extra fees"],
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const fetchFeatured = useCallback(async () => {
    try {
      const { data } = await api.get("/cars", { params: { featured: true, limit: 8 } });
      if (data && data.length > 0) {
        setFeatured(data);
        return;
      }
      // Fallback: show any approved cars when no featured ones exist
      const { data: fallback } = await api.get("/cars", { params: { limit: 8 } });
      setFeatured(fallback || []);
    } catch (err) {
      console.warn("[home] failed to load featured cars:", err);
    }
  }, []);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  const onSearch = (e) => {
    e.preventDefault();
    nav(`/cars${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A192F]">
        <img
          src={HERO_IMG}
          alt="Trusted used car in Kolkata"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 gj-hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl text-white gj-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F97316]/15 border border-[#F97316]/40 text-[#F97316] text-xs uppercase tracking-[0.2em] font-bold">
              <Sparkles className="w-3 h-3" /> West Bengal · Since 2026
            </span>
            <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Buy & Sell Used Cars Directly
              <br />
              <span className="text-[#F97316]">0% Brokerage.</span>
            </h1>
            <p className="mt-5 text-white/80 max-w-xl text-base leading-relaxed">
              Connect with verified dealers and sellers across West Bengal. Chat
              directly on WhatsApp — no middlemen, no hidden charges.
            </p>

            <form
              onSubmit={onSearch}
              data-testid="hero-search-form"
              className="mt-8 bg-white rounded-xl p-2 flex items-center gap-2 max-w-xl gj-shadow-card"
            >
              <Search className="w-5 h-5 text-[#0A192F]/60 ml-3 shrink-0" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by brand, model… e.g. Swift, Creta, Nexon"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-[#0A192F] placeholder:text-[#718096]"
                data-testid="hero-search-input"
              />
              <Button
                type="submit"
                className="bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold rounded-lg px-5"
                data-testid="hero-search-submit"
              >
                Search
              </Button>
            </form>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => nav("/cars")}
                className="bg-white hover:bg-white/90 text-[#0A192F] font-semibold rounded-lg px-6 py-6"
                data-testid="cta-browse-cars"
              >
                Find Your Next Car <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                onClick={() => nav("/sell")}
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-lg px-6 py-6 bg-transparent"
                data-testid="cta-sell-car"
              >
                List Your Car Free
              </Button>
              <Button
                onClick={() => nav("/dealer-onboarding")}
                variant="outline"
                className="border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-[#0A192F] rounded-lg px-6 py-6 bg-transparent"
                data-testid="cta-become-dealer"
              >
                Become a Dealer
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {TRUST.map((t) => (
                <div key={t.label} className="flex flex-col gap-2">
                  <t.icon className="w-5 h-5 text-[#F97316]" />
                  <div className="text-sm font-semibold text-white">{t.label}</div>
                  <div className="text-xs text-white/60 leading-snug hidden sm:block">{t.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY GADDI JUNCTION */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="why-gj-section">
        <div className="gj-divider-gold mb-4" />
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F] mb-10">
          Why Gaddi Junction?
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {COMPARE.map((col) => (
            <div
              key={col.title}
              data-testid={col.testid}
              className={`rounded-2xl p-8 border ${col.good ? "bg-[#0A192F] text-white border-[#F97316]/40" : "bg-white text-[#0A192F] border-[#0A192F]/10"}`}
            >
              <h3 className={`font-heading text-xl font-bold mb-6 ${col.good ? "text-[#F97316]" : ""}`}>{col.title}</h3>
              <ul className="space-y-4">
                {col.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm sm:text-base">
                    {col.good ? (
                      <CheckCircle2 className="w-5 h-5 text-[#F97316] shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                    )}
                    <span className={col.good ? "text-white/90" : "text-[#4A5568]"}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED — only rendered when real featured cars exist */}
      {featured.length > 0 && (
      <section className="bg-[#F3F0EA]/60" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="gj-divider-gold mb-4" />
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
                Featured cars this week
              </h2>
              <p className="text-[#4A5568] mt-2 text-sm sm:text-base">
                Hand-picked listings from our verified dealers across West Bengal.
              </p>
            </div>
            <Link
              to="/cars"
              className="text-sm font-medium text-[#0A192F] hover:text-[#F97316] inline-flex items-center gap-2"
              data-testid="featured-view-all"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 8).map((c) => (
              <CarCard key={c.id} car={c} featured />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* BRANDS — only shown when real featured inventory exists */}
      {featured.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="brands-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="gj-divider-gold mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">
              Shop by your favourite brand
            </h2>
            <p className="text-[#4A5568] mt-2 text-sm sm:text-base">
              From everyday Maruti's to feature-packed SUVs from Tata & Mahindra.
            </p>
          </div>
          <Link
            to="/cars"
            className="text-sm font-medium text-[#0A192F] hover:text-[#F97316] inline-flex items-center gap-2"
            data-testid="brands-view-all"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {BRANDS.map((b) => (
            <Link
              key={b.name}
              to={`/cars?brand=${encodeURIComponent(b.name)}`}
              data-testid={`brand-tile-${b.name.toLowerCase()}`}
              className="group bg-white border border-[#0A192F]/10 rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:-translate-y-1 hover:border-[#F97316] transition-all"
            >
              <div className="w-12 h-12 grid place-items-center bg-[#F3F0EA] rounded-lg overflow-hidden">
                <Car className="w-6 h-6 text-[#0A192F] group-hover:text-[#F97316] transition-colors" />
              </div>
              <div className="text-sm font-semibold text-[#0A192F]">{b.name}</div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* SELL CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="sell-cta-section">
        <div className="relative overflow-hidden rounded-2xl bg-[#0A192F] text-white p-8 md:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="gj-divider-gold mb-4" />
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Got a car to sell? <br />
              <span className="text-[#F97316]">Reach 10,000+ buyers</span> in Bengal.
            </h2>
            <p className="mt-4 text-white/70 max-w-md">
              List your car free for the first 30 days. Get serious WhatsApp
              leads directly from buyers — no middlemen.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() => (window.location.href = "/sell")}
                className="bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold rounded-lg px-6 py-6"
                data-testid="sell-cta-list-car"
              >
                List your car
              </Button>
              <Button
                onClick={() => (window.location.href = "/dealer-onboarding")}
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-lg px-6 py-6 bg-transparent"
                data-testid="sell-cta-register-dealer"
              >
                Register as Dealer
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "500+", v: "Cars listed" },
              { k: "120+", v: "Verified dealers" },
              { k: "8", v: "Cities in WB" },
              { k: "₹4 Cr+", v: "Deals enquired" },
            ].map((s) => (
              <div key={s.v} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="font-heading text-2xl font-extrabold text-[#F97316]">{s.k}</div>
                <div className="text-xs text-white/70 mt-1 uppercase tracking-[0.18em]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
